import {
  Button,
  Chip,
  Pagination,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react'
import { useMemo } from 'react'
import { FiEdit2, FiTrash2 } from 'react-icons/fi'

const ITEMS_PER_PAGE = 10

export default function BlockedNumbersTable({
  blocks,
  searchTerm = '',
  currentPage = 1,
  onPageChange,
  onEdit,
  onDelete,
  isLoading = false,
}) {
  // Filter blocks based on search term
  const filteredBlocks = useMemo(() => {
    if (!searchTerm)
      return blocks

    return blocks.filter(block =>
      block.blocked_number?.toLowerCase().includes(searchTerm.toLowerCase())
      || block.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      || block.notes?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [blocks, searchTerm])

  // Paginate filtered blocks
  const paginatedBlocks = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    return filteredBlocks.slice(startIndex, endIndex)
  }, [filteredBlocks, currentPage])

  const totalPages = Math.ceil(filteredBlocks.length / ITEMS_PER_PAGE)

  return (
    <>
      <div className="border">
        <Table aria-label="Blocked numbers table" removeWrapper radius="none">
          <TableHeader>
            <TableColumn>BLOCKED NUMBER</TableColumn>
            <TableColumn>TYPE</TableColumn>
            <TableColumn>REASON</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>CREATED</TableColumn>
            <TableColumn>ACTIONS</TableColumn>
          </TableHeader>
          <TableBody
            isLoading={isLoading}
            loadingContent={<Spinner />}
            emptyContent="No blocked numbers found"
          >
            {paginatedBlocks.map(block => (
              <TableRow key={block.id}>
                <TableCell className="font-mono text-sm">
                  {block.blocked_number}
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    color={block.is_global ? 'primary' : 'secondary'}
                    variant="flat"
                    radius="none"
                  >
                    {block.is_global ? 'Global' : 'Specific'}
                  </Chip>
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {block.reason || '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    color={block.is_active ? 'success' : 'default'}
                    variant="flat"
                    radius="none"
                  >
                    {block.is_active ? 'Active' : 'Inactive'}
                  </Chip>
                </TableCell>
                <TableCell className="text-sm text-gray-500">
                  {new Date(block.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      onPress={() => onEdit(block)}
                      radius="none"
                    >
                      <FiEdit2 size={14} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      onPress={() => onDelete(block)}
                      radius="none"
                      title="Delete permanently"
                    >
                      <FiTrash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            total={totalPages}
            page={currentPage}
            onChange={onPageChange}
            size="sm"
            radius="none"
          />
        </div>
      )}
    </>
  )
}
